import type { Scale } from '@tunasong/schemas'
import type { Chord, ChordType, ChordVariant, TriadType } from '../chord/index.js'
import { isChordVariant } from '../chord/index.js'
import { ScaleLib } from '../scale/index.js'
import type { ChordFunction } from './types.js'

/**
 * Map a plain Roman numeral to its flat-prefixed `ChordFunction` form.
 *
 * Why a table: only specific scale degrees have a defined flat-degree
 * counterpart in `ChordFunction`. ♭IV is exotic and unsupported.
 *
 * Both uppercase (major-quality) and lowercase (minor-quality) keys are
 * present because `prettyScaleChordDegree` lowercases the Roman numeral when
 * the chord built on that degree is minor or diminished — e.g. Locrian's
 * chord on the flat-3rd is D minor, which produces pretty='iii' and needs
 * to be flat-corrected to '♭iii'. Likewise Phrygian's chord on the flat-7th
 * is a minor triad, producing pretty='vii' that becomes '♭vii'.
 *
 * Uppercase 'V' → '♭V' supports Locrian's major triad on flat-5 (e.g. F
 * major in B Locrian). There is no lowercase '♭v' label because no mode in
 * v1.5's vocabulary produces a minor-quality chord on the flat-5th.
 *
 * The `'III+'` key supports harmonic minor's augmented chord on flat-3
 * (C augmented in A harmonic minor: C-E-G#). `prettyScaleChordDegree` emits
 * uppercase-with-plus for an augmented triad, then this table flattens it
 * because harmonic minor's intervals[2] is `'bIII'`.
 */
const FLAT_DEGREE_LABELS: Record<string, ChordFunction> = {
  II: '♭II',
  III: '♭III',
  V: '♭V',
  VI: '♭VI',
  VII: '♭VII',
  iii: '♭iii',
  vii: '♭vii',
  'III+': '♭III+',
}

/**
 * Map a plain Roman numeral to its sharp-prefixed `ChordFunction` form.
 *
 * Mirror of `FLAT_DEGREE_LABELS` for the `#` prefix. Single entry for v1.5:
 * Lydian's raised-4 diminished triad (F#° in C Lydian: F#-A-C). This is the
 * Lydian-characteristic chord — the raised 4 defines Lydian, so the chord
 * built on it deserves a label that calls out the alteration.
 *
 * The two tables are disjoint at v1.5 scope: no degree that's flatted in one
 * mode is sharpened in another within the supported scale set, so the
 * type-guard can consult SHARP first then FLAT without overlap concerns.
 */
const SHARP_DEGREE_LABELS: Record<string, ChordFunction> = {
  'iv°': '#iv°',
}

/**
 * Position in the `intervals` array → uppercase Roman numeral. Indices match
 * the convention in `lib/music-lib/src/scale/scale.ts` where intervals[0]=I,
 * intervals[1]=II, … intervals[6]=VII.
 */
const INTERVAL_INDEX_TO_DEGREE = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'] as const

/**
 * Returns true when the scale's natural form of `degree` is flatted relative
 * to the major scale (e.g., Dorian's III is `bIII`, Mixolydian's VII is
 * `bVII`). Reads the scale spec's `intervals` array directly — no semitone
 * arithmetic, no mode-name list to maintain. Any future minor-flavored scale
 * (or harmonic/melodic-minor variant) added to `SCALES` becomes correct
 * automatically as long as its intervals follow the existing `'bX'` convention.
 */
const scaleHasFlatDegree = (scale: Scale, degree: 'II' | 'III' | 'V' | 'VI' | 'VII'): boolean => {
  const { intervals } = ScaleLib.getScaleSpec(scale)
  const index = INTERVAL_INDEX_TO_DEGREE.indexOf(degree)
  return intervals[index] === `b${degree}`
}

/**
 * Returns true when the scale's natural form of `degree` is sharpened relative
 * to the major scale (e.g., Lydian's IV is `#IV`). Parallel to
 * `scaleHasFlatDegree`. At v1.5 scope only Lydian's IV qualifies.
 */
const scaleHasSharpDegree = (scale: Scale, degree: 'IV'): boolean => {
  const { intervals } = ScaleLib.getScaleSpec(scale)
  const index = INTERVAL_INDEX_TO_DEGREE.indexOf(degree)
  return intervals[index] === `#${degree}`
}

/**
 * Map a `ChordVariant.variant` to its underlying triad variant for diatonic
 * lookup. Returns `null` for chords without a triadic third (sus, power),
 * which forces the analyzer to fall through (borrowing → secondary → chromatic).
 *
 * The codebase only produces a finite set of `variant` strings — see
 * `lib/music-lib/src/chord/all-chords.ts` (triad × element composition) and
 * the parser in `chord-regexp.ts`. This table is exhaustive against that set.
 *
 * Why a table and not regex-stripping: variant strings are an irregular grammar
 * (`m7b5`, `7#5`, `madd9`, `7sus4`) and substring rules misclassify edge cases.
 */
const TRIAD_REDUCTION: Record<string, TriadType | null> = {
  // Plain triads — already a triad
  '': '',
  M: '',
  m: 'm',
  aug: 'aug',
  dim: 'dim',
  // 7th chords
  '7': '',
  maj7: '',
  m7: 'm',
  m7b5: 'dim',
  '7#5': 'aug',
  '7b5': '',
  '7#9': '',
  '7b9': '',
  // 6 / add / 9
  '6': '',
  m6: 'm',
  '9': '',
  m9: 'm',
  add9: '',
  madd9: 'm',
  // Sus / power: no triadic third — treat as non-diatonic for v1.
  '5': null,
  sus2: null,
  sus4: null,
  sus2sus4: null,
  '7sus2': null,
  '7sus4': null,
  '7sus2sus4': null,
}

/**
 * Reduce a chord to its triad. For ChordVariant inputs, look up the variant
 * and return a new chord with only the triad variant. For ChordNotes (notes-
 * driven chords) we cannot safely reduce — return the original; downstream
 * `getChordDegree` already short-circuits on ChordNotes.
 *
 * Returns `null` when the variant has no triadic third (sus, power) so the
 * analyzer can fall through.
 */
const reduceToTriad = (chord: Chord): Chord | null => {
  if (!isChordVariant(chord)) {
    return chord
  }
  const triadVariant = TRIAD_REDUCTION[chord.variant]
  if (triadVariant === undefined) {
    // Unknown variant — be conservative and pass through unmodified. If it
    // happens to be a triad we'll match; otherwise we'll return null and the
    // analyzer falls through to chromatic.
    return chord
  }
  if (triadVariant === null) {
    return null
  }
  return { ...chord, variant: triadVariant as ChordType } satisfies ChordVariant
}

/**
 * Dominant-7 variants that may function as secondary dominants. We refuse to
 * triad-reduce these *unless* the underlying triad is the diatonic V — in any
 * other position (e.g. `C7` in C major) the secondary-dominant analyzer
 * downstream can label them more usefully (`V/IV`, `V/V`, …). Without this
 * guard, reducing `C7` to `C` would short-circuit the analyzer at `I` and
 * lose the secondary-dominant reading.
 *
 * Altered dominants (`7#9`, `7b9`, `7#5`, `7b5`) belong to this set for the
 * same reason: `C7#9` in C major must NOT collapse to `I`. v1's secondary-
 * dominant detector still only matches plain `'7'`, so altered dominants at
 * non-V positions currently fall through to `chromatic` — that's honest. When
 * the detector is widened, those cases will get their `V/X` reading. The
 * guard's only effect is suppressing the misleading "we know wrong" verdict.
 */
const DOMINANT_7_VARIANTS: ReadonlySet<string> = new Set(['7', '7#5', '7b5', '7#9', '7b9'])

/**
 * True when the chord is a dominant-7 variant in the carved-out set
 * (`'7'`, `'7#5'`, `'7b5'`, `'7#9'`, `'7b9'`). Exposed for callers that
 * need to apply style-specific descriptions to dom7 chords (e.g. the blues
 * branch in `analyzeChord` swaps `I` / `IV` descriptions for dom7 chords).
 */
export const isDominantSeventhVariant = (chord: Chord): boolean =>
  isChordVariant(chord) && DOMINANT_7_VARIANTS.has(chord.variant)

export interface GetDiatonicFunctionOptions {
  /**
   * When `true`, the dom7-non-V short-circuit is suppressed — a dom7 chord
   * matches its diatonic degree directly without falling through to the
   * secondary-dominant analyzer. Set by `analyzeChord` when the caller signals
   * `context.style === 'blues'`, where I7 / IV7 / V7 are the primary chords
   * (not secondary dominants).
   *
   * Default: `false` (common-practice tonality, the v1 behavior).
   */
  allowDominantSevenths?: boolean
}

/**
 * If `chord` is a diatonic chord of `scale`, return its functional label
 * (e.g., 'I', 'ii', 'V', 'vii°'). Otherwise return null.
 *
 * Reduces 7th/9th/6th/add chords to their underlying triad before lookup —
 * `ScaleLib.getChordDegree` requires an exact triad match (see scale.ts:302).
 * Without reduction, `Cmaj7` in C major would miss `I` and the analyzer would
 * fall all the way through to `chromatic`.
 *
 * Special case for plain dominant-7 chords: we only accept them as diatonic V
 * (e.g. `G7` in C major). At any other degree we return null so the analyzer
 * can attempt a secondary-dominant reading (`C7` → `V/IV`, `D7` → `V/V`, …).
 * Callers passing `allowDominantSevenths: true` (blues style) opt out of this
 * carve-out — `C7` in C blues matches `I` directly.
 *
 * For flatted scale degrees (♭II in Phrygian/Locrian, ♭III in any minor-third
 * mode, ♭VI in Aeolian/Phrygian/Locrian/harmonic-minor, ♭VII in Dorian/
 * Mixolydian/Aeolian/Phrygian), `prettyScaleChordDegree` returns plain Roman
 * numerals — apply the flat marker here. We read the scale spec's `intervals`
 * array directly so any minor-flavored mode (not just `scale.type === 'minor'`)
 * is handled. See `scaleHasFlatDegree`.
 */
export const getDiatonicFunction = (
  chord: Chord,
  scale: Scale,
  options: GetDiatonicFunctionOptions = {}
): ChordFunction | null => {
  const triad = reduceToTriad(chord)
  if (!triad) {
    return null
  }
  const degree = ScaleLib.getChordDegree(scale, triad)
  if (!degree) {
    return null
  }
  // Dominant 7 only counts as diatonic when it lands on V — otherwise let the
  // secondary-dominant analyzer handle it. Suppressed in blues style.
  if (
    !options.allowDominantSevenths &&
    isChordVariant(chord) &&
    DOMINANT_7_VARIANTS.has(chord.variant) &&
    degree !== 'V'
  ) {
    return null
  }
  const pretty = ScaleLib.prettyScaleChordDegree(scale, degree)
  // Sharp-degree correction (Lydian's #iv° characteristic chord). Consult
  // SHARP_DEGREE_LABELS first because the two tables are disjoint at v1.5
  // scope — no degree is both sharp in one mode and flat in another within
  // the supported scale set.
  const sharpLabel = SHARP_DEGREE_LABELS[pretty]
  if (sharpLabel) {
    // pretty is currently only 'iv°' (Lydian's diminished raised-4). Strip
    // the `°` and uppercase to get the bare degree label for the spec lookup.
    const uppercaseDegree = pretty.replace('°', '').toUpperCase()
    if (uppercaseDegree === 'IV' && scaleHasSharpDegree(scale, uppercaseDegree)) {
      return sharpLabel
    }
  }
  const flatLabel = FLAT_DEGREE_LABELS[pretty]
  if (flatLabel) {
    // Map lowercase pretty (minor-quality chord on the degree) back to the
    // uppercase scale degree so `scaleHasFlatDegree` can read the interval.
    // Both Phrygian (chord on VII = minor → pretty 'vii') and Locrian (chord
    // on III = minor → pretty 'iii') need this path; their underlying
    // intervals are 'bVII' / 'bIII' respectively.
    //
    // Augmented form 'III+' (harmonic minor's flat-3 augmented) needs the `+`
    // stripped before the uppercase normalization so the degree-label lookup
    // matches.
    const uppercaseDegree = pretty.replace('+', '').toUpperCase()
    if (
      uppercaseDegree === 'II' ||
      uppercaseDegree === 'III' ||
      uppercaseDegree === 'V' ||
      uppercaseDegree === 'VI' ||
      uppercaseDegree === 'VII'
    ) {
      if (scaleHasFlatDegree(scale, uppercaseDegree)) {
        return flatLabel
      }
    }
  }
  return pretty as ChordFunction
}
